import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

import { useAdminStore } from '../store/useAdminStore';

function AdminLayout({ children }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { initDeviceId } = useAdminStore();

  const handleLogout = async () => {
    try {
      await fetch('/api/logout', {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID(), 'X-Requested-With': 'XMLHttpRequest' }
      });
    } catch(err) {
      console.error(err);
    } finally {
      // Clear persisted admin store to prevent stale data in the next session
      localStorage.removeItem('kediti-admin-storage');
      window.location.href = '/admin-portal';
    }
  };

  useEffect(() => {
    initDeviceId();

    // Google Translate Injection (Parity with Navbar)
    window.googleTranslateElementInit = () => {
      if (window.isGoogleTranslateInitializedAdmin) return;
      if (window.google && window.google.translate && typeof window.google.translate.TranslateElement === 'function') {
        window.isGoogleTranslateInitializedAdmin = true;
        try {
          new window.google.translate.TranslateElement({
            pageLanguage: 'en',
            includedLanguages: 'en,hi',
            autoDisplay: false
          }, 'google_translate_element_admin');
        } catch (e) {
          console.error("Google Translate initialization error:", e);
        }
      }
    };

    if (!document.querySelector('script[src*="translate_a/element.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.defer = true;
      document.body.appendChild(script);
    } else {
      if (window.googleTranslateElementInit) {
        window.googleTranslateElementInit();
      }
    }
  }, [initDeviceId]);

  return (
    <div className="flex h-screen w-screen bg-gray-950 text-white font-sans overflow-hidden">
      
      {/* 📱 MOBILE OVERLAY */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* 🛡️ SIDEBAR */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 border-r border-gray-800 flex flex-col transform transition-transform duration-300 lg:static lg:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-black tracking-tighter text-blue-500">OLYMPIADS</span>
            <span className="text-xs font-mono bg-blue-900/50 text-blue-400 px-2 py-0.5 rounded border border-blue-800/50">ADMIN</span>
          </div>
          <button className="lg:hidden text-gray-400 hover:text-white" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close Menu">
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        <nav className="flex-1 px-4 space-y-2 mt-4 overflow-y-auto">
          <NavLink onClick={() => setIsMobileMenuOpen(false)} to="/admin/database" className={({isActive}) => `block px-4 py-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-blue-600 font-bold shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>
             Registrations
          </NavLink>
          <NavLink onClick={() => setIsMobileMenuOpen(false)} to="/admin/cms" className={({isActive}) => `block px-4 py-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-blue-600 font-bold shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>
             Content & Updates
          </NavLink>
          <NavLink onClick={() => setIsMobileMenuOpen(false)} to="/admin/crm" className={({isActive}) => `block px-4 py-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-blue-600 font-bold shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>
             Ticketing Hub
          </NavLink>
          <NavLink onClick={() => setIsMobileMenuOpen(false)} to="/admin/devops" className={({isActive}) => `block px-4 py-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-red-600 font-bold shadow-[0_0_15px_rgba(220,38,38,0.4)]' : 'text-red-400 hover:bg-gray-800 hover:text-red-300'}`}>
             🛠️ DevOps & Infra
          </NavLink>
          <NavLink onClick={() => setIsMobileMenuOpen(false)} to="/admin/security" className={({isActive}) => `block px-4 py-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-yellow-600 font-bold shadow-[0_0_15px_rgba(202,138,4,0.4)]' : 'text-yellow-400 hover:bg-gray-800 hover:text-yellow-300'}`}>
             🛡️ Threat Board
          </NavLink>
          <NavLink onClick={() => setIsMobileMenuOpen(false)} to="/admin/marketing" className={({isActive}) => `block px-4 py-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-blue-600 font-bold shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'text-blue-400 hover:bg-gray-800 hover:text-blue-300'}`}>
             🎯 Marketing & SEO
          </NavLink>
        </nav>

        <div className="p-4 border-t border-gray-800">
          <button onClick={handleLogout} className="w-full py-2 text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded transition-colors duration-200">
            Secure Logout
          </button>
        </div>
      </aside>

      {/* 🚀 MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col h-full overflow-hidden w-full relative">
        {/* TOPBAR */}
        <header className="h-16 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-gray-400 hover:text-white" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open Menu">
              <Menu size={24} aria-hidden="true" />
            </button>
            <h2 className="text-lg font-semibold tracking-wide text-gray-200 hidden sm:block">Admin Portal</h2>
          </div>
          
          <div className="flex items-center gap-4">

            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.7)]"></span>
              <span className="text-xs font-mono text-gray-400 hidden lg:inline">SYS_HEALTH: OPTIMAL</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-purple-600 flex items-center justify-center font-bold shadow-lg shadow-blue-500/20">
              A
            </div>
            <div id="google_translate_element_admin" className="hidden lg:block"></div>
          </div>
        </header>

        {/* PAGE CONTENT */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8 bg-gray-950">
          {children}
        </div>
      </main>
    </div>
  );
}

export default AdminLayout;
